import { FormEvent, MouseEvent, useEffect, useState } from 'react';
import { askTourStep, TOUR_ASK_FALLBACK } from '@/hooks/useTourAsk';
import styles from './ProductDemoTourOverlay.module.css';

const CHIPS = [
  { id: 'what', label: 'What does this do?', question: 'What does this do?' },
  { id: 'when', label: 'When would I use this?', question: 'When would I use this?' },
] as const;

type TourStepAskProps = {
  featureId: string;
  stepId: string;
  stepTitle: string;
  stepBody: string;
};

export default function TourStepAsk({ featureId, stepId, stepTitle, stepBody }: TourStepAskProps) {
  const [input, setInput] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInput('');
    setAnswer(null);
    setError(null);
    setIsLoading(false);
  }, [stepId]);

  const sendQuestion = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;

    setIsLoading(true);
    setError(null);
    setAnswer(null);

    try {
      const message = await askTourStep({
        question: trimmed,
        featureId,
        stepId,
        stepTitle,
        stepBody,
      });
      setAnswer(message);
    } catch {
      setError(TOUR_ASK_FALLBACK);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void sendQuestion(input);
  };

  const handleChipClick = (event: MouseEvent<HTMLButtonElement>, question: string) => {
    event.preventDefault();
    event.stopPropagation();
    void sendQuestion(question);
  };

  return (
    <div className={styles.tourAsk} data-testid="tour-step-ask">
      <p className={styles.tourAskLabel}>Ask about this step</p>
      <div className={styles.tourAskChips} role="group" aria-label="Suggested questions">
        {CHIPS.map(chip => (
          <button
            key={chip.id}
            type="button"
            className={styles.tourAskChip}
            disabled={isLoading}
            onClick={event => handleChipClick(event, chip.question)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <form className={styles.tourAskForm} onSubmit={handleSubmit}>
        <label className={styles.tourAskSrOnly} htmlFor={`tour-ask-input-${stepId}`}>
          Ask a question about this step
        </label>
        <input
          id={`tour-ask-input-${stepId}`}
          className={styles.tourAskInput}
          value={input}
          onChange={event => setInput(event.target.value)}
          placeholder="Ask a short question…"
          maxLength={400}
          disabled={isLoading}
        />
        <button type="submit" className={styles.tourAskSend} disabled={isLoading || !input.trim()}>
          Send
        </button>
      </form>
      {(isLoading || error || answer) && (
        <div
          className={`${styles.tourAskReply} ${error ? styles.tourAskReplyError : ''}`}
          data-testid="tour-step-ask-answer"
          role={error ? 'alert' : 'status'}
        >
          <p className={styles.tourAskReplyKicker}>{error ? 'Guide unavailable' : 'Guide'}</p>
          <p className={styles.tourAskReplyText}>{isLoading ? 'Thinking…' : error || answer}</p>
        </div>
      )}
    </div>
  );
}
